import type { Text } from 'troika-three-text'
import { disposeCadText, scheduleCadTextSync } from '@/plugins/cad/diagnostics/cadTextSync'
import type { MTextEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { createCadText } from '@/plugins/cad/module/CADModule/object/text/CadTextFactory'
import { getLoggerManager } from '@/shared/logging'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

const log = getLoggerManager().getLogger('cad:diagnostics')
const TEXT_DIAGNOSTIC_INTERVAL_MS = 15000
const LONG_TEXT_LENGTH = 1000

let createdTextObjects = 0
let disposedTextObjects = 0
let outlineTextObjects = 0
let textSyncCalls = 0
let lastTextDiagnosticAt = 0

function getTextLength(entity: MTextEntity): number {
  return typeof entity.contents === 'string' ? entity.contents.length : 0
}

function logTextDiagnostic(reason: string, entity: MTextEntity, force = false): void {
  const now = getLoggerManager().now()
  const textLength = getTextLength(entity)
  const shouldLog =
    force ||
    textLength >= LONG_TEXT_LENGTH ||
    createdTextObjects % 100 === 0 ||
    now - lastTextDiagnosticAt >= TEXT_DIAGNOSTIC_INTERVAL_MS

  if (!shouldLog) {
    return
  }
  lastTextDiagnosticAt = now
  log.info(
    `[CADText] ${reason}, entityId=${entity.id}, textLength=${textLength}, fontSize=${entity.textHeight}, maxWidth=${entity.rectWidth}, created=${createdTextObjects}, disposed=${disposedTextObjects}, outlines=${outlineTextObjects}, syncCalls=${textSyncCalls}`
  )
}

/**
 * 多行文本实体的渲染对象管理器，基于 troika-three-text 渲染 MText。
 */
export class TextObjectManager extends BaseObjectManager<MTextEntity, Text> {
  // 用于高亮的对象
  private outlineObjectMap: Map<string, Text> = new Map()

  protected createObject(entity: MTextEntity) {
    const object = createCadText()
    object.whiteSpace = 'normal'
    object.overflowWrap = 'break-word'
    object.maxWidth = entity.rectWidth
    object.text = entity.contents
    object.fontSize = entity.textHeight
    object.color = `#${entity.color.toString(16).padStart(6, '0')}`
    object.position.set(entity.position.x, entity.position.y, 0)
    object.rotation.z = entity.rotation
    object.anchorX = 'left'
    object.anchorY = 'top'
    const materialOption = MaterialUtils.getMaterialOptionByEntity(entity)
    object.material.depthTest = materialOption.depthTest ?? true
    object.material.depthWrite = materialOption.depthWrite ?? true
    scheduleCadTextSync(object)
    createdTextObjects += 1
    textSyncCalls += 1
    logTextDiagnostic('createObject sync', entity)
    return object
  }

  protected onUpdateGeometry(entity: MTextEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    if (
      object.maxWidth === entity.rectWidth &&
      object.text === entity.contents &&
      object.fontSize === entity.textHeight &&
      object.position.x === entity.position.x &&
      object.position.y === entity.position.y &&
      object.rotation.z === entity.rotation
    )
      return
    object.maxWidth = entity.rectWidth
    object.text = entity.contents
    object.fontSize = entity.textHeight
    object.position.set(entity.position.x, entity.position.y, 0)
    object.rotation.z = entity.rotation
    scheduleCadTextSync(object)
    textSyncCalls += 1
    logTextDiagnostic('updateGeometry sync', entity)
    // 更新轮廓线几何体
    this.onUpdateOutlineGeometry(entity)
  }

  protected onUpdateMaterial(entity: MTextEntity): void {
    const object = this.objectMap.get(entity.id)
    if (!object) {
      return
    }
    const color = `#${entity.color.toString(16).padStart(6, '0')}`
    if (object.color === color && object.fontSize === entity.textHeight) return
    object.color = color
    object.fontSize = entity.textHeight
    scheduleCadTextSync(object)
    textSyncCalls += 1
    logTextDiagnostic('updateMaterial sync', entity)
    this.onUpdateOutlineGeometry(entity)
  }

  protected disposeObject(object: Text): void {
    disposeCadText(object)
    disposedTextObjects += 1
    if (object.userData.entity) {
      const entity = object.userData.entity as MTextEntity
      const outlineObject = this.outlineObjectMap.get(entity.id)
      outlineObject?.parent?.remove(outlineObject)
      if (outlineObject) {
        disposeCadText(outlineObject)
        disposedTextObjects += 1
      }
      this.outlineObjectMap.delete(entity.id)
      logTextDiagnostic('disposeObject', entity)
    }
  }

  protected handleEntityHighlight(entity: MTextEntity, mode: HighlightMode): void {
    const object = this.objectMap.get(entity.id) as Text
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      // 取消高亮时，移除轮廓线
      const outlineObject = this.outlineObjectMap.get(entity.id)
      if (outlineObject) {
        outlineObject.visible = false
      }
    } else {
      // 高亮时，添加轮廓线
      const color =
        mode === HighlightMode.Hover
          ? this.hoverMaterialOption.color
          : this.selectMaterialOption.color
      const colorString = `#${color.toString(16).padStart(6, '0')}`
      const outlineObject = this.outlineObjectMap.get(entity.id) as Text
      if (!outlineObject) {
        const outlineObject = this.createObject(entity)
        outlineObject.userData.cadTextOutline = true
        outlineObject.color = colorString
        outlineObject.material.depthTest = false
        outlineObject.fillOpacity = this.hoverMaterialOption.opacity
        this.group.add(outlineObject)
        this.outlineObjectMap.set(entity.id, outlineObject)
        outlineTextObjects += 1
        logTextDiagnostic('createOutline sync', entity, true)
      } else {
        outlineObject.color = colorString
        outlineObject.visible = true
      }
    }
  }

  private onUpdateOutlineGeometry(entity: MTextEntity): void {
    const object = this.objectMap.get(entity.id) as Text
    if (!object) {
      return
    }
    const outlineObject = this.outlineObjectMap.get(entity.id)
    if (!outlineObject) {
      return
    }
    outlineObject.text = entity.contents
    outlineObject.fontSize = entity.textHeight
    outlineObject.maxWidth = entity.rectWidth
    outlineObject.rotation.z = entity.rotation
    outlineObject.position.set(entity.position.x, entity.position.y, 0)
    scheduleCadTextSync(outlineObject)
    textSyncCalls += 1
    logTextDiagnostic('updateOutline sync', entity)
  }
}
